import { useMemo, useState } from "react";
import {
  Address,
  IsoCountryCode,
  ISO_3166_1_ALPHA_2_CODES,
  isISO31661Alpha2Code,
} from "@forgepoint/shared";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

/**
 * Props for the AddressEditSheet component.
 */
interface AddressEditSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  address: Address | null;
  onSave: (address: Address) => void;
}

/**
 * Creates an empty address object with all fields set to null.
 * @returns A new Address with all fields null
 */
const createEmptyAddress = (): Address => ({
  name: null,
  address_line_1: null,
  address_line_2: null,
  city: null,
  state: null,
  zipcode: null,
  country: null,
});

/**
 * Slide-over sheet for editing a company's address with fields for each address component.
 * @param props - Sheet props including current address and save callback
 * @returns Address edit sheet element
 */
export function AddressEditSheet({
  open,
  onOpenChange,
  address,
  onSave,
}: AddressEditSheetProps) {
  const [localAddress, setLocalAddress] = useState<Address>(
    address ?? createEmptyAddress(),
  );

  const countryOptions = useMemo(
    () =>
      ISO_3166_1_ALPHA_2_CODES.map(
        (code): { code: IsoCountryCode; label: string } => ({
          code,
          label: code,
        }),
      ),
    [],
  );

  /**
   * Resets local state when the sheet opens.
   * @param nextOpen - Whether the sheet should be open
   */
  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen) {
      setLocalAddress(address ?? createEmptyAddress());
    }
    onOpenChange(nextOpen);
  };

  /**
   * Updates a single field on the local address.
   * @param key - The address field to update
   * @param value - The new value
   */
  const updateField = <K extends keyof Address>(key: K, value: Address[K]) => {
    setLocalAddress((prev) => ({ ...prev, [key]: value }));
  };

  /**
   * Handles country selection with ISO validation.
   * @param value - The selected country code string
   */
  const handleCountrySelect = (value: string) => {
    if (value === "") {
      updateField("country", null);
      return;
    }
    if (!isISO31661Alpha2Code(value)) {
      return;
    }
    updateField("country", value);
  };

  /**
   * Saves the local address and closes the sheet.
   */
  const handleSave = () => {
    onSave(localAddress);
    onOpenChange(false);
  };

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetContent className="flex w-full flex-col p-0 sm:max-w-md">
        <SheetHeader className="border-b px-6 py-4">
          <SheetTitle>Edit Address</SheetTitle>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-6 pb-6 pt-3">
          <div className="grid gap-3">
            <div className="grid gap-1.5">
              <Label htmlFor="addr-line1">Address Line 1</Label>
              <Input
                id="addr-line1"
                placeholder="Street address"
                value={localAddress.address_line_1 || ""}
                onChange={(e) => {
                  const v = e.target.value;
                  updateField("address_line_1", v || null);
                }}
              />
            </div>

            <div className="grid gap-1.5">
              <Label htmlFor="addr-line2">Address Line 2</Label>
              <Input
                id="addr-line2"
                placeholder="Apt, suite, unit, etc."
                value={localAddress.address_line_2 || ""}
                onChange={(e) => {
                  const v = e.target.value;
                  updateField("address_line_2", v || null);
                }}
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="grid gap-1.5">
                <Label htmlFor="addr-city">City</Label>
                <Input
                  id="addr-city"
                  placeholder="City"
                  value={localAddress.city || ""}
                  onChange={(e) => {
                    const v = e.target.value;
                    updateField("city", v || null);
                  }}
                />
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor="addr-state">State</Label>
                <Input
                  id="addr-state"
                  placeholder="State"
                  value={localAddress.state || ""}
                  onChange={(e) => {
                    const v = e.target.value;
                    updateField("state", v || null);
                  }}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="grid gap-1.5">
                <Label htmlFor="addr-zip">Zip Code</Label>
                <Input
                  id="addr-zip"
                  placeholder="Zip code"
                  value={localAddress.zipcode || ""}
                  onChange={(e) => {
                    const v = e.target.value;
                    updateField("zipcode", v || null);
                  }}
                />
              </div>
              <div className="grid gap-1.5">
                <Label>Country</Label>
                <Select
                  value={localAddress.country || ""}
                  onValueChange={handleCountrySelect}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select country" />
                  </SelectTrigger>
                  <SelectContent>
                    {countryOptions.map(({ code, label }) => (
                      <SelectItem key={code} value={code}>
                        {label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 border-t p-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave}>Save Address</Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
